import * as React from "react"
import { Clock } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

/**
 * TimePicker - A reusable time picker that replaces native <input type="time">.
 *
 * Native time inputs have two well-known bugs in modals:
 *   1. The picker popup can overlay and intercept clicks on sibling buttons
 *      (e.g. Cancel / Generate in a Dialog footer).
 *   2. On some browsers it refuses to dismiss on blur.
 *
 * This component solves both by rendering compact HH/MM grids inside a
 * Popover. No native picker is ever opened and no scrolling is needed —
 * all 24 hours and all minute steps fit in view at once.
 *
 * @param {string}   props.value       - "HH:MM" (24-hour). Empty string allowed.
 * @param {function} props.onChange    - (value: "HH:MM") => void
 * @param {boolean}  props.disabled    - Disable the trigger.
 * @param {number}   props.minuteStep  - Default 5. Use 1 for free entry.
 * @param {string}   props.className   - Extra trigger className.
 * @param {string}   props.testId      - data-testid on the trigger button.
 */
function TimePicker({
  value = "",
  onChange,
  disabled = false,
  minuteStep = 5,
  className,
  testId,
}) {
  const [open, setOpen] = React.useState(false)

  const [hh, mm] = React.useMemo(() => {
    const match = /^([0-2]?\d):([0-5]?\d)$/.exec(value || "")
    if (!match) return ["", ""]
    return [
      String(Math.min(23, parseInt(match[1], 10))).padStart(2, "0"),
      String(Math.min(59, parseInt(match[2], 10))).padStart(2, "0"),
    ]
  }, [value])

  const hours = React.useMemo(
    () => Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")),
    []
  )
  const minutes = React.useMemo(() => {
    const step = Math.max(1, Math.min(30, minuteStep))
    const out = []
    for (let m = 0; m < 60; m += step) out.push(String(m).padStart(2, "0"))
    return out
  }, [minuteStep])

  const setHour = (h) => {
    onChange(`${h}:${mm || "00"}`)
  }
  const setMinute = (m) => {
    onChange(`${hh || "00"}:${m}`)
    // Close after minute pick — hour is usually chosen first
    setOpen(false)
  }

  const displayText = hh && mm ? `${hh}:${mm}` : "--:--"

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "justify-start text-left font-mono h-8 px-2 text-xs",
            !value && "text-muted-foreground",
            className
          )}
          data-testid={testId}
        >
          <Clock className="mr-1.5 h-3 w-3 shrink-0" />
          {displayText}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="end">
        <div className="flex items-start gap-4">
          {/* Hours — 24 buttons in a 6-col grid, no scrolling */}
          <div data-testid={testId ? `${testId}-hours` : undefined}>
            <div className="mb-1.5 text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
              Hour
            </div>
            <div className="grid grid-cols-6 gap-1">
              {hours.map((h) => {
                const active = h === hh
                return (
                  <button
                    key={h}
                    type="button"
                    data-active={active}
                    onClick={() => setHour(h)}
                    className={cn(
                      "w-8 h-7 rounded text-xs font-mono hover:bg-accent transition-colors",
                      active && "bg-primary text-primary-foreground font-semibold hover:bg-primary"
                    )}
                  >
                    {h}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Minutes — 12 buttons (at 5-min step) in a 3-col grid, no scrolling */}
          <div
            className="border-l pl-4"
            data-testid={testId ? `${testId}-minutes` : undefined}
          >
            <div className="mb-1.5 text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
              Minute
            </div>
            <div className="grid grid-cols-3 gap-1">
              {minutes.map((m) => {
                const active = m === mm
                return (
                  <button
                    key={m}
                    type="button"
                    data-active={active}
                    onClick={() => setMinute(m)}
                    className={cn(
                      "w-8 h-7 rounded text-xs font-mono hover:bg-accent transition-colors",
                      active && "bg-primary text-primary-foreground font-semibold hover:bg-primary"
                    )}
                  >
                    {m}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

TimePicker.displayName = "TimePicker"

export { TimePicker }
